import { LockClosedIcon, GlobeAltIcon, CalendarDaysIcon, ViewfinderCircleIcon, EnvelopeIcon, BoltIcon, MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon, BellAlertIcon, DocumentDuplicateIcon, DocumentTextIcon, ChartBarSquareIcon, LinkIcon, FolderIcon, TagIcon, CloudIcon, RectangleGroupIcon, } from '@heroicons/react/24/outline';
/**
 * Default scoped sidebar for the launch products that have their own
 * monitoring schedulers — SSL, DNS, Domain Expiry, Blacklist, Email
 * Deliverability. Each gets an Overview, Alerting and Reports section.
 * Individual products can override by supplying their own sidebar field.
 */
const monitoringSidebar = [
    {
        title: 'Overview',
        items: [
            { label: 'Dashboard', path: 'dashboard', Icon: Squares2X2Icon },
            { label: 'Targets', path: 'domains', Icon: ListBulletIcon },
        ],
    },
    {
        title: 'Alerting',
        items: [
            { label: 'Rules', path: 'alerting/rules', Icon: BellAlertIcon },
            { label: 'Templates', path: 'alerting/templates', Icon: DocumentDuplicateIcon },
            { label: 'Delivery Log', path: 'alerting/log', Icon: DocumentTextIcon },
        ],
    },
    {
        title: 'Reports',
        items: [
            { label: 'History & Exports', path: 'reports', Icon: ChartBarSquareIcon },
        ],
    },
];
export const productCatalog = [
    {
        slug: 'ssl',
        name: 'SSL Wide Watch',
        shortName: 'SSL Monitor',
        tagline: 'Track every cert, catch every expiry.',
        Icon: LockClosedIcon,
        availability: 'SUBSCRIBED',
        accent: { var: '--royal', softVar: '--lav-50' },
        sidebar: [
            {
                title: 'Overview',
                items: [
                    { label: 'Dashboard', path: 'dashboard', Icon: Squares2X2Icon },
                    { label: 'Domains', path: 'domains', Icon: ListBulletIcon },
                    { label: 'Groups', path: 'groups', Icon: RectangleGroupIcon },
                ],
            },
            {
                title: 'Alerting',
                items: [
                    { label: 'Rules', path: 'alerting/rules', Icon: BellAlertIcon },
                    { label: 'Templates', path: 'alerting/templates', Icon: DocumentDuplicateIcon },
                    { label: 'Delivery Log', path: 'alerting/log', Icon: DocumentTextIcon },
                ],
            },
            {
                title: 'Reports',
                items: [
                    { label: 'History & Exports', path: 'reports', Icon: ChartBarSquareIcon },
                ],
            },
        ],
    },
    {
        slug: 'dns',
        name: 'DNS Wide Watch',
        shortName: 'DNS Monitor',
        tagline: 'Record drift, NS changes, propagation alerts.',
        Icon: GlobeAltIcon,
        availability: 'COMING_SOON',
        availabilityBadge: { text: 'SOON', kind: 'soon' },
        accent: { var: '--emerald-hi', softVar: '--mint-100' },
        sidebar: monitoringSidebar,
    },
    {
        slug: 'domain-expiry',
        name: 'Domain Expiry',
        shortName: 'Domain Expiry',
        tagline: 'Keep every domain registration from lapsing.',
        Icon: CalendarDaysIcon,
        availability: 'COMING_SOON',
        accent: { var: '--butter-glow', softVar: '--butter-50' },
        sidebar: monitoringSidebar,
    },
    {
        slug: 'blacklist',
        name: 'Blacklist Monitor',
        shortName: 'Blacklist',
        tagline: 'Know the moment you land on an RBL.',
        Icon: ViewfinderCircleIcon,
        availability: 'COMING_SOON',
        accent: { var: '--crimson', softVar: '--rose-glow' },
        sidebar: monitoringSidebar,
    },
    {
        slug: 'email-deliverability',
        name: 'Email Deliverability',
        shortName: 'Email Deliverability',
        tagline: 'SPF, DKIM, DMARC, and inbox-placement signals.',
        Icon: EnvelopeIcon,
        availability: 'COMING_SOON',
        availabilityBadge: { text: 'SOON', kind: 'soon' },
        accent: { var: '--sky-glow', softVar: '--lav-50' },
        sidebar: monitoringSidebar,
    },
    {
        slug: 'uptime',
        name: 'Uptime Wide Watch',
        shortName: 'Uptime',
        tagline: 'Continuous HTTP/HTTPS monitoring; alerts when sites go down.',
        Icon: BoltIcon,
        availability: 'SUBSCRIBED',
        accent: { var: '--peach-glow', softVar: '--butter-50' },
        sidebar: [
            {
                title: 'Overview',
                items: [
                    { label: 'Monitors', path: 'monitors', Icon: ListBulletIcon },
                    { label: 'Groups', path: 'groups', Icon: Squares2X2Icon },
                ],
            },
            {
                title: 'Alerting',
                items: [
                    { label: 'Rules', path: 'alerting/rules', Icon: BellAlertIcon },
                    { label: 'Templates', path: 'alerting/templates', Icon: DocumentDuplicateIcon },
                    { label: 'Delivery Log', path: 'alerting/log', Icon: DocumentTextIcon },
                ],
            },
            {
                title: 'Reports',
                items: [
                    { label: 'History & Exports', path: 'reports', Icon: ChartBarSquareIcon },
                ],
            },
            // Recipients used to live here as a per-product page. V013 moved
            // recipients into platform-wide Alert Groups (/alerts/groups) since
            // they're cross-product. The link is gone; users land on Alert
            // Groups via the platform sidebar instead.
        ],
    },
    {
        slug: 'url-shortener',
        name: 'URL Shortener',
        shortName: 'URL Shortener',
        tagline: 'Branded short links, click analytics, UTM presets.',
        Icon: LinkIcon,
        availability: 'SUBSCRIBED',
        availabilityBadge: { text: 'NEW', kind: 'new' },
        accent: { var: '--rose-glow', softVar: '--rose-50' },
        sidebar: [
            {
                title: 'Links',
                items: [
                    { label: 'All links', path: 'links', Icon: ListBulletIcon },
                    { label: 'Folders', path: 'folders', Icon: FolderIcon },
                ],
            },
            {
                title: 'Configuration',
                items: [
                    { label: 'UTM presets', path: 'utm-presets', Icon: TagIcon },
                    { label: 'Custom domains', path: 'domains', Icon: CloudIcon },
                ],
            },
            {
                title: 'Reports',
                items: [
                    { label: 'History & Exports', path: 'reports', Icon: ChartBarSquareIcon },
                ],
            },
        ],
    },
    {
        slug: 'port-scanner',
        name: 'Port Scanner',
        shortName: 'Port Scanner',
        tagline: 'Weekly exposure sweeps across your public IPs.',
        Icon: MagnifyingGlassIcon,
        availability: 'COMING_SOON',
        availabilityBadge: { text: "'27", kind: 'yr' },
        accent: { var: '--ink-500', softVar: '--line' },
        sidebar: monitoringSidebar,
    },
];
export function findProduct(slug) {
    return productCatalog.find((p) => p.slug === slug);
}
export function subscribedProducts() {
    return productCatalog.filter((p) => p.availability === 'SUBSCRIBED');
}
